import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/App';
import { useConfig } from '@/context/ConfigContext';
import { useApiData, useApiMutation } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import ReactMarkdown from 'react-markdown';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { 
  Users, Activity, TrendingUp, Upload, Plus, RefreshCw, 
  Target, Clock, UserCheck, AlertTriangle, CheckCircle2, XCircle,
  FileSpreadsheet, Calendar, BarChart3, Sparkles, Brain, FileDown,
  FileType, ClipboardPaste, Loader2, Copy, Trash2, Pencil, CalendarPlus,
  AlertCircle, FileUp
} from 'lucide-react';
import { ExportDropdown } from '@/components/ExportDropdown';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';
import DataSyncStatus from '@/components/DataSyncStatus';
import { DataHistoryPanel } from '@/components/DataHistory';

// Import extracted components and constants
import {
  API_URL,
  COLORS,
  TEAMS,
  DISCIPLINES,
  getTeamColor,
  FUTURISTIC_GRADIENTS,
  getDefaultNewClinicianData,
  getDefaultNewCapacityData,
  getDefaultNewDisciplineData,
  getDefaultDuplicateData,
  getNextMedicareWeekFromPeriod,
  formatMedicareWeek,
  TeamBadge,
  OutcomeBadge,
  StatusPulse,
  NeonCard,
  NeonMetricCard,
  NeonHeader,
  NeonSectionHeader,
  NeonProgress,
  NeonTableWrapper,
  AIInsightsTab,
  ProductivityTab,
  CapacityTab,
  DisciplineTab,
  HistoricalTrendsTab,
  AnalyticsTab,
  // Dialog components
  AddClinicianDialog,
  AddCapacityDialog,
  AddDisciplineDialog,
  EditEntryDialog,
  DeleteConfirmDialog,
  BulkDeleteDialog,
  NewWeekDialog,
  DuplicateWeekDialog,
  PasteDataDialog,
  UploadAnalyzeDialog,
  SimpleUploadDialog,
  // Trend components
  TeamTrendChart,
  ClinicianProductivityTable,
  // Caseload management
  CaseloadSection
} from '@/components/rncm-productivity';

export default function RNCMProductivity() {
  const { token, user } = useAuth();
  const { getRNCMCaseloadTarget, getWorkforceTarget } = useConfig();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [capacityData, setCapacityData] = useState([]);
  const [disciplineSummaries, setDisciplineSummaries] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  
  // Get configurable targets from context
  const caseloadTarget = useMemo(() => getRNCMCaseloadTarget(), [getRNCMCaseloadTarget]);
  const optimalVisitsPerWeek = useMemo(() => getWorkforceTarget('optimal_visits_per_week', 18), [getWorkforceTarget]);
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [processingPaste, setProcessingPaste] = useState(false);
  const reportRef = useRef(null);;
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDisciplineDialog, setShowDisciplineDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [selectedCapacityPeriod, setSelectedCapacityPeriod] = useState('');
  const [selectedProductivityPeriod, setSelectedProductivityPeriod] = useState('');
  const [duplicateType, setDuplicateType] = useState('productivity'); // 'productivity' or 'capacity'
  const [duplicating, setDuplicating] = useState(false);
  
  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'productivity', 'capacity', 'discipline'
  const [deleting, setDeleting] = useState(false);
  
  // Edit state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editType, setEditType] = useState(''); // 'productivity', 'capacity'
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Bulk delete state
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Staff roster management state
  const [staffRoster, setStaffRoster] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffEditTarget, setStaffEditTarget] = useState(null);
  const [staffEditData, setStaffEditData] = useState({ team: '', discipline: '' });
  const [showStaffEditDialog, setShowStaffEditDialog] = useState(false);
  const [showStaffDeleteDialog, setShowStaffDeleteDialog] = useState(false);
  const [staffDeleteTarget, setStaffDeleteTarget] = useState(null);
  const [staffActionLoading, setStaffActionLoading] = useState(false);

  // Schedule Intelligence state
  const [scheduleFile, setScheduleFile] = useState(null);
  const [scheduleAnalysis, setScheduleAnalysis] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [ptoEntries, setPtoEntries] = useState([]);
  const [ptoName, setPtoName] = useState('');
  const [ptoDate, setPtoDate] = useState('');
  const [ptoDateEnd, setPtoDateEnd] = useState('');
  const [idgNurses, setIdgNurses] = useState([]);
  const [idgName, setIdgName] = useState('');
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [unplannedVisits, setUnplannedVisits] = useState({});
  const [unplannedDate, setUnplannedDate] = useState('');
  const [unplannedCount, setUnplannedCount] = useState('');
  
  // New week state
  const [showNewWeekDialog, setShowNewWeekDialog] = useState(false);
  const [newWeekType, setNewWeekType] = useState('');
  const [newWeekDates, setNewWeekDates] = useState(null);
  const [loadingNewWeek, setLoadingNewWeek] = useState(false);
  
  // Clinician list for dropdown
  const [clinicianList, setClinicianList] = useState([]);
  const [productivityPeriods, setProductivityPeriods] = useState([]);
  const [capacityPeriods, setCapacityPeriods] = useState([]);
  
  // Enhanced upload state
  const [uploadAnalysisResult, setUploadAnalysisResult] = useState(null);
  const [uploadingWithAnalysis, setUploadingWithAnalysis] = useState(false);
  const [uploadPeriod, setUploadPeriod] = useState(new Date().toISOString().split('T')[0]);
  const uploadFileRef = useRef(null);
  const [uploadTextContent, setUploadTextContent] = useState('');
  
  // Duplicate week state
  const [duplicateData, setDuplicateData] = useState(getDefaultDuplicateData());
  
  const [newClinicianData, setNewClinicianData] = useState(getDefaultNewClinicianData());
  
  const [newCapacityData, setNewCapacityData] = useState(getDefaultNewCapacityData());

  const [newDisciplineData, setNewDisciplineData] = useState(getDefaultNewDisciplineData());

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager' || user?.role === 'staff';

  const fetchData = async () => {
    try {
      const [analyticsRes, cliniciansRes, capacityRes, disciplineRes, clinicianListRes, prodPeriodsRes, capPeriodsRes] = await Promise.all([
        fetch(`${API_URL}/api/productivity/analytics`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API_URL}/api/productivity/clinicians?limit=50`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API_URL}/api/capacity/rncm?limit=50`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API_URL}/api/productivity/discipline-summary?limit=12`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API_URL}/api/capacity/clinicians`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API_URL}/api/productivity/periods?limit=12`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API_URL}/api/capacity/periods?limit=12`, { headers: { Authorization: `Bearer ${token}` }})
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (cliniciansRes.ok) setClinicians(await cliniciansRes.json());
      if (capacityRes.ok) setCapacityData(await capacityRes.json());
      if (disciplineRes.ok) setDisciplineSummaries(await disciplineRes.json());
      if (clinicianListRes.ok) {
        const data = await clinicianListRes.json();
        setClinicianList(data.clinicians || []);
      }
      if (prodPeriodsRes.ok) setProductivityPeriods(await prodPeriodsRes.json());
      if (capPeriodsRes.ok) setCapacityPeriods(await capPeriodsRes.json());
    } catch (error) {
      toast.error('Failed to load productivity data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchStaffRoster();
    fetchScheduleHistory();
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success('Data refreshed');
  };

  // Handle duplicate week - creates a new Medicare week
  const handleDuplicateWeek = async () => {
    if (!duplicateData.source_period_start) {
      toast.error('Please select a source week');
      return;
    }
    
    setDuplicating(true);
    try {
      const endpoint = duplicateType === 'productivity' 
        ? `${API_URL}/api/productivity/clinician/duplicate-week`
        : `${API_URL}/api/capacity/rncm/duplicate-week`;
      
      const params = new URLSearchParams({
        source_period_start: duplicateData.source_period_start,
        target_period_start: duplicateData.target_period_start,
        target_period_end: duplicateData.target_period_end
      });
      
      const response = await fetch(`${endpoint}?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        const weekDisplay = formatMedicareWeek(duplicateData.target_period_start, duplicateData.target_period_end);
        toast.success(`New Medicare week created: ${weekDisplay}`, { 
          description: `${result.count} staff entries created. Switching to new week view.` 
        });
        
        // Auto-switch to the newly created period
        if (duplicateType === 'capacity') {
          setSelectedCapacityPeriod(duplicateData.target_period_start);
        } else {
          setSelectedProductivityPeriod(duplicateData.target_period_start);
        }
        
        setShowDuplicateDialog(false);
        setDuplicateData(getDefaultDuplicateData());
        await fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to create week', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error creating new week');
    } finally {
      setDuplicating(false);
    }
  };

  // Handle clinician selection from dropdown
  const handleClinicianSelect = (clinicianName, formType) => {
    const selectedClinician = clinicianList.find(c => c.name === clinicianName);
    if (selectedClinician) {
      if (formType === 'productivity') {
        setNewClinicianData(prev => ({
          ...prev,
          clinician_name: selectedClinician.name,
          team: selectedClinician.team || prev.team,
          hire_date: selectedClinician.hire_date || prev.hire_date,
          discipline: selectedClinician.discipline || prev.discipline
        }));
      } else {
        setNewCapacityData(prev => ({
          ...prev,
          clinician_name: selectedClinician.name,
          team: selectedClinician.team || prev.team,
          hire_date: selectedClinician.hire_date || prev.hire_date
        }));
      }
    }
  };

  const handleAddClinician = async () => {
    try {
      const response = await fetch(`${API_URL}/api/productivity/clinician`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newClinicianData)
      });

      if (response.ok) {
        toast.success('Clinician productivity added');
        setShowAddDialog(false);
        setNewClinicianData({
          clinician_name: '', hire_date: '', team: '', discipline: 'RNCM',
          period_start: new Date().toISOString().split('T')[0],
          period_end: new Date().toISOString().split('T')[0],
          total_visits: 0, average_time_per_visit: 0, expectation_min: 15, expectation_max: 18, comment: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to add', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error adding data');
    }
  };

  const handleAddCapacity = async () => {
    try {
      const response = await fetch(`${API_URL}/api/capacity/rncm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newCapacityData)
      });

      if (response.ok) {
        toast.success('RNCM capacity added');
        setShowCapacityDialog(false);
        setNewCapacityData({
          clinician_name: '', hire_date: '', team: '',
          period_start: new Date().toISOString().split('T')[0],
          period_end: new Date().toISOString().split('T')[0],
          visit_capacity: 0, expectation_min: 15, expectation_max: 20, 
          burnout_threshold: 19, max_capacity: 20, comment: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to add', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error adding data');
    }
  };

  const handleAddDisciplineSummary = async () => {
    const total = newDisciplineData.rncm_visits + newDisciplineData.lpn_visits + 
                  newDisciplineData.wound_rn_visits + newDisciplineData.prn_visits;
    
    try {
      const response = await fetch(`${API_URL}/api/productivity/discipline-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...newDisciplineData, total_visits: total })
      });

      if (response.ok) {
        toast.success('Discipline summary added');
        setShowDisciplineDialog(false);
        setNewDisciplineData({
          period_start: new Date().toISOString().split('T')[0],
          period_end: new Date().toISOString().split('T')[0],
          rncm_visits: 0, lpn_visits: 0, wound_rn_visits: 0, prn_visits: 0
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to add', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error adding data');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/productivity/batch-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Upload successful', { 
          description: `${result.records_created.clinicians} clinician records, ${result.records_created.capacity} capacity records` 
        });
        setShowUploadDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Upload failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Upload error');
    }
  };

  // Enhanced upload with AI analysis
  const handleUploadWithAnalysis = async () => {
    const file = uploadFileRef.current?.files[0];
    if (!file && !uploadTextContent.trim()) {
      toast.error('Please upload a file or paste data');
      return;
    }

    setUploadingWithAnalysis(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      if (uploadTextContent.trim()) {
        formData.append('text_content', uploadTextContent);
      }
      formData.append('period_start', uploadPeriod);

      const response = await fetch(`${API_URL}/api/upload-analyze/rncm-productivity`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadAnalysisResult(result);
        toast.success('Data uploaded and analyzed', {
          description: `${result.clinician_records_count || 0} clinician records extracted`
        });
        fetchData(); // Refresh the main data
      } else {
        const error = await response.json();
        toast.error('Upload failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Upload error');
    } finally {
      setUploadingWithAnalysis(false);
    }
  };

  const handleCreatePIPFromUpload = async (pipIndex) => {
    if (!uploadAnalysisResult?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/upload-analyze/${uploadAnalysisResult.id}/create-pip?pip_index=${pipIndex}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`PIP "${result.pip.title}" created`);
      } else {
        toast.error('Failed to create PIP');
      }
    } catch (error) {
      toast.error('Error creating PIP');
    }
  };

  const resetUploadAnalysis = () => {
    setUploadAnalysisResult(null);
    setUploadTextContent('');
    if (uploadFileRef.current) uploadFileRef.current.value = '';
  };

  const handlePasteData = async () => {
    if (!pasteText.trim()) {
      toast.error('Please paste some data');
      return;
    }
    
    setProcessingPaste(true);
    try {
      const response = await fetch(`${API_URL}/api/productivity/parse-paste`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: pasteText })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Data imported', { 
          description: `${result.records_created.capacity} capacity records, ${result.records_created.productivity} productivity records` 
        });
        setShowPasteDialog(false);
        setPasteText('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Import failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Import error');
    } finally {
      setProcessingPaste(false);
    }
  };

  // Delete handlers
  const confirmDelete = (record, type) => {
    setDeleteTarget(record);
    setDeleteType(type);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget || !deleteType) return;
    
    setDeleting(true);
    try {
      let endpoint = '';
      if (deleteType === 'productivity') {
        endpoint = `${API_URL}/api/productivity/clinician/${deleteTarget.id}`;
      } else if (deleteType === 'capacity') {
        // Use the correct endpoint for RNCM capacity deletion
        endpoint = `${API_URL}/api/capacity/rncm/${deleteTarget.id}`;
      } else if (deleteType === 'discipline') {
        endpoint = `${API_URL}/api/productivity/discipline-summary/${deleteTarget.id}`;
      }
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`${deleteType.charAt(0).toUpperCase() + deleteType.slice(1)} entry deleted`);
        setShowDeleteDialog(false);
        setDeleteTarget(null);
        setDeleteType('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Delete failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error deleting entry');
    } finally {
      setDeleting(false);
    }
  };

  // Edit handlers
  const openEditDialog = (record, type) => {
    setEditTarget(record);
    setEditType(type);
    setEditData({ ...record });
    setShowEditDialog(true);
  };

  const handleEdit = async () => {
    if (!editTarget || !editType) return;
    
    setEditing(true);
    try {
      let endpoint = '';
      if (editType === 'productivity') {
        endpoint = `${API_URL}/api/productivity/clinician/${editTarget.id}`;
      } else if (editType === 'capacity') {
        endpoint = `${API_URL}/api/capacity/rncm/${editTarget.id}`;
      }
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(editData)
      });
      
      if (response.ok) {
        toast.success(`${editType.charAt(0).toUpperCase() + editType.slice(1)} entry updated`);
        setShowEditDialog(false);
        setEditTarget(null);
        setEditType('');
        setEditData({});
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Update failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error updating entry');
    } finally {
      setEditing(false);
    }
  };

  // Bulk delete handlers
  const openBulkDeleteDialog = (type) => {
    setBulkDeleteType(type);
    setShowBulkDeleteDialog(true);
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      let endpoint = '';
      if (bulkDeleteType === 'productivity') {
        endpoint = `${API_URL}/api/productivity/clinician/bulk/all`;
      } else if (bulkDeleteType === 'capacity') {
        endpoint = `${API_URL}/api/capacity/rncm/bulk/all`;
      }
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowBulkDeleteDialog(false);
        setBulkDeleteType('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Bulk delete failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error during bulk delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ==================== STAFF ROSTER MANAGEMENT ====================
  const fetchStaffRoster = async () => {
    setStaffLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/staff?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const roster = data.staff || data.roster || data || [];
        setStaffRoster(Array.isArray(roster) ? roster : []);
      }
    } catch (error) {
      console.error('Failed to fetch staff roster:', error);
    } finally {
      setStaffLoading(false);
    }
  };

  const openStaffEdit = (staff) => {
    setStaffEditTarget(staff);
    setStaffEditData({
      team: staff.team || '',
      discipline: staff.discipline || staff.role || 'RN',
      name: staff.name || '',
      status: staff.status || 'active'
    });
    setShowStaffEditDialog(true);
  };

  const saveStaffEdit = async () => {
    if (!staffEditTarget) return;
    setStaffActionLoading(true);
    try {
      const staffId = staffEditTarget.id || staffEditTarget.name;
      const response = await fetch(`${API_URL}/api/staff/${encodeURIComponent(staffId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(staffEditData)
      });
      if (response.ok) {
        toast.success(`${staffEditTarget.name} updated. Team/discipline synced across all records.`);
        setShowStaffEditDialog(false);
        fetchStaffRoster();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Update failed');
      }
    } catch (error) {
      toast.error('Failed to update staff');
    } finally {
      setStaffActionLoading(false);
    }
  };

  const confirmStaffDelete = (staff) => {
    setStaffDeleteTarget(staff);
    setShowStaffDeleteDialog(true);
  };

  const handleStaffDelete = async () => {
    if (!staffDeleteTarget) return;
    setStaffActionLoading(true);
    try {
      const staffId = staffDeleteTarget.id || staffDeleteTarget.name;
      const response = await fetch(`${API_URL}/api/staff/${encodeURIComponent(staffId)}?permanent=true`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `${staffDeleteTarget.name} deleted from all records`);
        setShowStaffDeleteDialog(false);
        setStaffDeleteTarget(null);
        fetchStaffRoster();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Delete failed');
      }
    } catch (error) {
      toast.error('Failed to delete staff');
    } finally {
      setStaffActionLoading(false);
    }
  };

  // ==================== SCHEDULE INTELLIGENCE ====================
  const analyzeSchedule = async () => {
    if (!scheduleFile) { toast.error('Please select a schedule file'); return; }
    setScheduleLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', scheduleFile);
      if (ptoEntries.length > 0) {
        formData.append('pto_json', JSON.stringify(ptoEntries));
      }
      if (idgNurses.length > 0) {
        formData.append('idg_json', JSON.stringify(idgNurses));
      }
      if (Object.keys(unplannedVisits).length > 0) {
        formData.append('unplanned_json', JSON.stringify(unplannedVisits));
      }
      const response = await fetch(`${API_URL}/api/schedule-intelligence/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setScheduleAnalysis(data.analysis);
        toast.success(`Schedule analyzed: ${data.visits_parsed} visits, ${data.analysis.weekly_totals.total_rns} RNs`);
        fetchScheduleHistory();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Failed to analyze schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const addPtoEntry = () => {
    if (!ptoName || !ptoDate) { toast.error('Enter RN name and start date'); return; }
    const endDate = ptoDateEnd || ptoDate;
    // Generate an entry for each day in the range
    const start = new Date(ptoDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    if (end < start) { toast.error('End date must be on or after start date'); return; }
    const newEntries = [];
    const d = new Date(start);
    while (d <= end) {
      newEntries.push({ rn_name: ptoName, date: d.toISOString().split('T')[0] });
      d.setDate(d.getDate() + 1);
    }
    setPtoEntries(prev => [...prev, ...newEntries]);
    setPtoName(''); setPtoDate(''); setPtoDateEnd('');
    toast.success(`Added ${newEntries.length} PTO day${newEntries.length > 1 ? 's' : ''} for ${ptoName}`);
  };


  const fetchScheduleHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/schedule-intelligence/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScheduleHistory(data.analyses || []);
      }
    } catch (error) {
      console.error('Failed to fetch schedule history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const deleteScheduleAnalysis = async (analysisId) => {
    try {
      const response = await fetch(`${API_URL}/api/schedule-intelligence/history/${analysisId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Analysis deleted');
        fetchScheduleHistory();
      }
    } catch { toast.error('Failed to delete'); }
  };

  const exportSchedulePDF = () => {
    if (!scheduleAnalysis) return;
    const a = scheduleAnalysis;
    const days = a.per_day || [];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Build calendar cells from per_day data
    const calendarCells = days.map(d => {
      const dt = new Date(d.date + 'T12:00:00');
      return { ...d, dayName: dayNames[dt.getDay()], dayNum: dt.getDate(), month: dt.toLocaleString('default', { month: 'long' }), year: dt.getFullYear() };
    });

    const monthLabel = calendarCells.length > 0 ? `${calendarCells[0].month} ${calendarCells[0].year}` : '';
    const startDow = calendarCells.length > 0 ? new Date(calendarCells[0].date + 'T12:00:00').getDay() : 0;

    // Build flags by date for quick lookup
    const flagsByDate = {};
    (a.flags || []).forEach(f => {
      if (!flagsByDate[f.date]) flagsByDate[f.date] = [];
      flagsByDate[f.date].push(f);
    });

    const cellHtml = (cell) => {
      const hasFlags = (flagsByDate[cell.date] || []).length > 0;
      const prnTotal = cell.net_prn_gap || cell.prn_visits_needed || 0;
      const prnShifts = cell.prn_shifts || 0;
      const prnHours = cell.prn_hours || 0;
      const unplanned = cell.unplanned_visits || 0;
      const bgColor = prnTotal > 4 ? '#fecaca' : prnTotal > 0 ? '#fef3c7' : '#d1fae5';
      const borderColor = prnTotal > 4 ? '#ef4444' : prnTotal > 0 ? '#f59e0b' : '#10b981';
      const flagsHtml = (flagsByDate[cell.date] || []).map(f => `<div style="font-size:10px;color:#dc2626;">&#9888; ${f.rn}: ${f.visits} visits (+${f.over_by})</div>`).join('');

      return `<td style="vertical-align:top;padding:8px;border:1px solid #e5e7eb;background:${bgColor};width:14.28%;height:160px;">
        <div style="font-weight:700;font-size:16px;color:#1f2937;margin-bottom:6px;">${cell.dayNum} <span style="font-size:11px;font-weight:400;color:#6b7280;">${cell.dayName}</span></div>
        <div style="font-size:12px;color:#374151;margin-bottom:2px;"><strong>${cell.total_visits}</strong> total visits</div>
        <div style="font-size:12px;color:#374151;margin-bottom:2px;"><strong>${cell.rns_working}</strong> RNs working</div>
        ${cell.overflow_visits > 0 ? `<div style="font-size:12px;color:#ea580c;font-weight:600;">${cell.overflow_visits} overflow</div>` : ''}
        ${cell.pto_visits > 0 ? `<div style="font-size:12px;color:#d97706;font-weight:600;">${cell.pto_visits} PTO visits</div>` : ''}
        ${unplanned > 0 ? `<div style="font-size:12px;color:#dc2626;font-weight:600;">${unplanned} unplanned</div>` : ''}
        <div style="margin-top:6px;padding:4px 8px;border-radius:6px;background:${prnTotal > 0 ? borderColor : '#059669'};color:white;font-weight:700;font-size:13px;text-align:center;">
          ${prnTotal > 0 ? `${prnTotal} PRN visits` : 'No PRN'}
        </div>
        ${prnShifts > 0 ? `<div style="text-align:center;font-size:11px;color:${borderColor};font-weight:700;margin-top:2px;">${prnShifts} shift${prnShifts > 1 ? 's' : ''} (${prnHours}h)</div>` : ''}
        ${flagsHtml}
      </td>`;
    };

    // Build empty cells for start of week alignment
    const emptyCells = Array(startDow).fill('<td style="border:1px solid #f3f4f6;background:#f9fafb;height:130px;"></td>').join('');

    // Structural overload section
    const overloadHtml = (a.structural_overload || []).map(so =>
      `<div style="padding:8px 14px;background:#fef2f2;border-left:4px solid #ef4444;border-radius:4px;margin-bottom:6px;font-size:13px;color:#991b1b;">
        <strong>${so.rn}</strong> — over limit ${so.days_over}/${so.total_days} days (${so.pct_over}%). Panel restructuring recommended.
      </div>`
    ).join('');

    // Per-RN table
    const perRnRows = (a.per_rn || []).map(r => {
      const utilColor = r.utilization_pct > 100 ? '#ef4444' : r.utilization_pct > 80 ? '#f59e0b' : '#10b981';
      return `<tr>
        <td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;font-weight:500;">${r.rn}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;text-align:center;">${r.days_worked}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;text-align:center;">${r.weekly_capacity}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;text-align:center;font-weight:600;">${r.visits_scheduled}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;text-align:center;color:${r.overflow > 0 ? '#ef4444' : '#10b981'};font-weight:600;">${r.overflow > 0 ? '+' + r.overflow : '0'}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;text-align:center;color:${r.pto_days > 0 ? '#d97706' : '#9ca3af'};">${r.pto_days || '-'}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #f3f4f6;text-align:center;"><span style="background:${utilColor};color:white;padding:2px 8px;border-radius:10px;font-size:12px;font-weight:600;">${r.utilization_pct}%</span></td>
      </tr>`;
    }).join('');

    const wt = a.weekly_totals || {};
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Schedule Intelligence Report</title>
    <style>
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { size: landscape; margin: 0.5in; } }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1f2937; padding: 20px; }
      .header { background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0d9488 100%); color: white; padding: 24px 30px; border-radius: 12px; margin-bottom: 20px; }
      .header h1 { font-size: 24px; margin-bottom: 4px; }
      .header .sub { color: #94a3b8; font-size: 13px; }
      .summary-bar { display: flex; gap: 12px; margin-bottom: 20px; }
      .summary-card { flex: 1; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px; text-align: center; }
      .summary-card .val { font-size: 28px; font-weight: 800; color: #0f172a; }
      .summary-card .lbl { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
      table.calendar { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      table.calendar th { background: #0f172a; color: white; padding: 8px; font-size: 12px; text-align: center; }
      .section-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 20px 0 10px; padding-bottom: 6px; border-bottom: 2px solid #0d9488; }
      table.data { width: 100%; border-collapse: collapse; font-size: 13px; }
      table.data th { background: #f1f5f9; padding: 8px 10px; text-align: left; font-weight: 600; color: #475569; border-bottom: 2px solid #e2e8f0; }
      .footer { margin-top: 20px; text-align: center; color: #9ca3af; font-size: 11px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
      .legend { display: flex; gap: 16px; margin: 10px 0; font-size: 12px; }
      .legend-item { display: flex; align-items: center; gap: 4px; }
      .legend-dot { width: 14px; height: 14px; border-radius: 3px; }
    </style></head><body>
      <div class="header">
        <h1>Schedule Intelligence Report</h1>
        <div class="sub">Goodwin Hospice &mdash; ${a.date_range?.start || ''} to ${a.date_range?.end || ''} &mdash; Generated ${new Date().toLocaleDateString()}</div>
      </div>

      <div class="summary-bar">
        <div class="summary-card"><div class="val">${wt.total_rns || 0}</div><div class="lbl">RNs Scheduled</div></div>
        <div class="summary-card"><div class="val">${wt.total_visits || 0}</div><div class="lbl">Total Visits</div></div>
        <div class="summary-card"><div class="val">${a.flags?.length || 0}</div><div class="lbl">Overload Flags</div></div>
        <div class="summary-card"><div class="val" style="color:#ef4444;">${wt.total_prn_visit_demand || 0}</div><div class="lbl">PRN Visit Demand</div></div>
        <div class="summary-card"><div class="val" style="color:#ef4444;">${wt.total_pto_visits || 0}</div><div class="lbl">PTO Visits</div></div>
      </div>

      <div class="legend">
        <div class="legend-item"><div class="legend-dot" style="background:#d1fae5;border:1px solid #10b981;"></div> No PRN needed</div>
        <div class="legend-item"><div class="legend-dot" style="background:#fef3c7;border:1px solid #f59e0b;"></div> PRN needed (1-4 visits)</div>
        <div class="legend-item"><div class="legend-dot" style="background:#fecaca;border:1px solid #ef4444;"></div> Heavy PRN needed (5+ visits)</div>
      </div>

      <div class="section-title">Weekly Calendar View</div>
      <table class="calendar">
        <thead><tr>
          ${dayNames.map(d => `<th>${d}</th>`).join('')}
        </tr></thead>
        <tbody><tr>
          ${emptyCells}
          ${calendarCells.map((cell, i) => {
            const pos = startDow + i;
            const newRow = (pos > 0 && pos % 7 === 0) ? '</tr><tr>' : '';
            return newRow + cellHtml(cell);
          }).join('')}
        </tr></tbody>
      </table>

      ${overloadHtml ? `<div class="section-title">Structural Overload Alerts</div>${overloadHtml}` : ''}

      <div class="section-title">Per-RN Capacity Analysis</div>
      <table class="data">
        <thead><tr>
          <th>RN</th><th style="text-align:center;">Days</th><th style="text-align:center;">Capacity</th>
          <th style="text-align:center;">Scheduled</th><th style="text-align:center;">Overflow</th>
          <th style="text-align:center;">PTO</th><th style="text-align:center;">Utilization</th>
        </tr></thead>
        <tbody>${perRnRows}</tbody>
      </table>

      <div style="margin-top:16px;padding:14px 20px;background:#f0f9ff;border:1px solid #0ea5e9;border-radius:8px;">
        <strong>Weekly PRN Recommendation:</strong> ${wt.total_prn_visit_demand || 0} PRN visits needed (${wt.total_rn_overflow || 0} overflow + ${wt.total_pto_visits || 0} PTO + ${wt.total_unplanned_visits || 0} unplanned)
        &rarr; <strong style="color:#ef4444;">${wt.weekly_prns_needed || 0} PRN nurse${(wt.weekly_prns_needed || 0) !== 1 ? 's' : ''}</strong> for the week.
        Peak day: <strong>${wt.peak_day?.date || 'N/A'}</strong> (${wt.peak_day?.prn_visits || 0} visits, ${wt.peak_day?.prn_nurses || 0} nurses).
      </div>

      <div class="footer">Generated by CareFlow Intelligence&trade; &mdash; Goodwin Hospice Schedule Intelligence</div>
    </body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
    toast.success('PDF ready for printing');
  };

  // New week handlers
  const openNewWeekDialog = async (type) => {
    setNewWeekType(type);
    setLoadingNewWeek(true);
    setShowNewWeekDialog(true);
    
    try {
      let endpoint = '';
      if (type === 'productivity') {
        endpoint = `${API_URL}/api/productivity/clinician/new-week`;
      } else if (type === 'capacity') {
        endpoint = `${API_URL}/api/capacity/rncm/new-week`;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setNewWeekDates(result);
        // Pre-fill the add form with new week dates
        if (type === 'productivity') {
          setNewClinicianData(prev => ({
            ...prev,
            period_start: result.period_start,
            period_end: result.period_end
          }));
        } else if (type === 'capacity') {
          setNewCapacityData(prev => ({
            ...prev,
            period_start: result.period_start,
            period_end: result.period_end
          }));
        }
      } else {
        const error = await response.json();
        toast.error('Failed to calculate new week', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error calculating new week');
    } finally {
      setLoadingNewWeek(false);
    }
  };

  const startNewWeekEntry = () => {
    setShowNewWeekDialog(false);
    if (newWeekType === 'productivity') {
      setShowAddDialog(true);
    } else if (newWeekType === 'capacity') {
      setShowCapacityDialog(true);
    }
  };

  const runAIAnalysis = async () => {
    setAnalyzingAI(true);
    try {
      const response = await fetch(`${API_URL}/api/productivity/ai-analysis`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setAiAnalysis(result);
        toast.success('AI Analysis complete');
      } else {
        const error = await response.json();
        toast.error('Analysis failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Analysis error');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const exportPDF = () => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    pdf.setFontSize(20);
    pdf.setTextColor(79, 70, 229);
    pdf.text('RNCM Productivity & Capacity Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Summary
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Summary', 14, yPos);
    yPos += 8;
    
    pdf.setFontSize(10);
    pdf.text(`Total Clinicians: ${analytics?.summary?.total_clinicians || 0}`, 14, yPos);
    yPos += 6;
    pdf.text(`Total Capacity: ${capacityData.reduce((s, c) => s + (c.visit_capacity || 0), 0)}`, 14, yPos);
    yPos += 6;
    pdf.text(`Over Capacity: ${capacityData.filter(c => c.planning_outcome === 'Over Capacity').length}`, 14, yPos);
    yPos += 6;
    pdf.text(`Within Threshold: ${capacityData.filter(c => c.planning_outcome === 'Within Threshold').length}`, 14, yPos);
    yPos += 15;

    // Capacity Data Table
    pdf.setFontSize(14);
    pdf.text('RN Capacity Data', 14, yPos);
    yPos += 8;

    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Clinician', 14, yPos);
    pdf.text('Team', 80, yPos);
    pdf.text('Capacity', 110, yPos);
    pdf.text('Expectation', 140, yPos);
    pdf.text('Outcome', 170, yPos);
    yPos += 6;

    pdf.setTextColor(0, 0, 0);
    capacityData.forEach(c => {
      if (yPos > 270) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.text(c.clinician_name?.substring(0, 30) || '', 14, yPos);
      pdf.text(c.team || '', 80, yPos);
      pdf.text(String(c.visit_capacity), 110, yPos);
      pdf.text(`${c.expectation_min}-${c.expectation_max}`, 140, yPos);
      pdf.text(c.planning_outcome || '', 170, yPos);
      yPos += 5;
    });

    // AI Analysis if available
    if (aiAnalysis?.analysis) {
      pdf.addPage();
      yPos = 20;
      pdf.setFontSize(14);
      pdf.text('AI Analysis', 14, yPos);
      yPos += 10;
      
      pdf.setFontSize(9);
      const lines = pdf.splitTextToSize(aiAnalysis.analysis, pageWidth - 28);
      lines.forEach(line => {
        if (yPos > 280) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.text(line, 14, yPos);
        yPos += 5;
      });
    }

    pdf.save(`rncm_productivity_report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported');
  };

  const exportWord = async () => {
    const sections = [];

    // Title
    sections.push(
      new Paragraph({
        children: [new TextRun({ text: 'RNCM Productivity & Capacity Report', bold: true, size: 48, color: '4F46E5' })],
        heading: HeadingLevel.TITLE,
      }),
      new Paragraph({
        children: [new TextRun({ text: `Generated: ${new Date().toLocaleString()}`, size: 20, color: '666666' })]
      }),
      new Paragraph({ text: '' })
    );

    // Summary
    sections.push(
      new Paragraph({ children: [new TextRun({ text: 'Summary', bold: true, size: 28 })], heading: HeadingLevel.HEADING_1 }),
      new Paragraph({ children: [new TextRun({ text: `Total Clinicians: ${analytics?.summary?.total_clinicians || 0}` })] }),
      new Paragraph({ children: [new TextRun({ text: `Total Capacity: ${capacityData.reduce((s, c) => s + (c.visit_capacity || 0), 0)}` })] }),
      new Paragraph({ children: [new TextRun({ text: `Over Capacity: ${capacityData.filter(c => c.planning_outcome === 'Over Capacity').length}` })] }),
      new Paragraph({ children: [new TextRun({ text: `Within Threshold: ${capacityData.filter(c => c.planning_outcome === 'Within Threshold').length}` })] }),
      new Paragraph({ text: '' })
    );

    // Capacity Table
    sections.push(
      new Paragraph({ children: [new TextRun({ text: 'RN Capacity Data', bold: true, size: 28 })], heading: HeadingLevel.HEADING_1 })
    );

    const tableRows = [
      new DocxTableRow({
        children: ['Clinician', 'Team', 'Capacity', 'Expectation', 'Outcome'].map(h => 
          new DocxTableCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] })
        )
      }),
      ...capacityData.map(c => new DocxTableRow({
        children: [
          c.clinician_name || '',
          c.team || '',
          String(c.visit_capacity || 0),
          `${c.expectation_min}-${c.expectation_max}`,
          c.planning_outcome || ''
        ].map(text => new DocxTableCell({ children: [new Paragraph({ children: [new TextRun({ text })] })] }))
      }))
    ];

    sections.push(
      new DocxTable({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: tableRows }),
      new Paragraph({ text: '' })
    );

    // AI Analysis
    if (aiAnalysis?.analysis) {
      sections.push(
        new Paragraph({ children: [new TextRun({ text: 'AI Analysis', bold: true, size: 28 })], heading: HeadingLevel.HEADING_1 })
      );
      
      aiAnalysis.analysis.split('\n').forEach(line => {
        sections.push(new Paragraph({ children: [new TextRun({ text: line })] }));
      });
    }

    const doc = new Document({ sections: [{ properties: {}, children: sections }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `rncm_productivity_report_${new Date().toISOString().split('T')[0]}.docx`);
    toast.success('Word document exported');
  };

  const copyToClipboard = () => {
    let text = 'CLINICIANS\tHire Date\tTEAM\tRNCM VISIT CAPACITY\tExpectation\tPlanning Outcome\tCOMMENT\n';
    capacityData.forEach(c => {
      text += `${c.clinician_name}\t${c.hire_date || ''}\t${c.team}\t${c.visit_capacity}\t${c.expectation_min}-${c.expectation_max}\t${c.planning_outcome}\t${c.comment || ''}\n`;
    });
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="productivity-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const outcomeData = analytics?.productivity_outcomes ? [
    { name: 'Within Threshold', value: analytics.productivity_outcomes['Within Threshold'] || 0, color: '#10B981' },
    { name: 'Above Threshold', value: analytics.productivity_outcomes['Above Threshold'] || 0, color: '#3B82F6' },
    { name: 'Below Threshold', value: analytics.productivity_outcomes['Below Threshold'] || 0, color: '#EF4444' }
  ] : [];

  const capacityOutcomeData = analytics?.capacity_outcomes ? [
    { name: 'Within Threshold', value: analytics.capacity_outcomes['Within Threshold'] || 0, color: '#10B981' },
    { name: 'Over Capacity', value: analytics.capacity_outcomes['Over Capacity'] || 0, color: '#F59E0B' },
    { name: 'Under Capacity', value: analytics.capacity_outcomes['Under Capacity'] || 0, color: '#EF4444' }
  ] : [];

  return (
    <div className="space-y-8 futuristic-scrollbar" data-testid="rncm-productivity-page" ref={reportRef}>
      {/* Futuristic Neon Header */}
      <NeonHeader
        icon={Users}
        title="RNCM Productivity & Capacity"
        subtitle="Real-time workforce analytics powered by AI intelligence"
        gradient="from-cyan-500 via-purple-500 to-pink-500"
      >
        <div className="flex gap-3 flex-wrap">
          <ExportDropdown reportType="productivity" token={token} />
          <Button 
            variant="secondary" 
            className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-all hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20" 
            onClick={handleRefresh} 
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="secondary" 
            className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-all hover:scale-105" 
            onClick={copyToClipboard}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy
          </Button>
          {canEdit && (
            <>
              <Button 
                variant="secondary" 
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-all hover:scale-105" 
                onClick={() => setShowDuplicateDialog(true)}
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                New Week
              </Button>
              <Button 
                variant="secondary" 
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-all hover:scale-105" 
                onClick={() => setShowPasteDialog(true)}
              >
                <ClipboardPaste className="h-4 w-4 mr-2" />
                Paste Data
              </Button>
              <Button 
                variant="secondary" 
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-all hover:scale-105" 
                onClick={() => setShowUploadDialog(true)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
              <Button 
                onClick={() => setShowAddDialog(true)} 
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white border-0 shadow-lg shadow-cyan-500/30 transition-all hover:scale-105 hover:shadow-cyan-400/50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Entry
              </Button>
            </>
          )}
        </div>
      </NeonHeader>

      {/* Data Sync Status */}
      <DataSyncStatus showFixButton={true} />

      {/* Futuristic Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <NeonMetricCard
          title="Total Clinicians"
          value={analytics?.summary?.total_clinicians || 0}
          subtitle="Active staff members"
          icon={UserCheck}
          gradient="cyan"
        />
        <NeonMetricCard
          title="Capacity Records"
          value={analytics?.summary?.capacity_records || analytics?.summary?.total_records || 0}
          subtitle="This period"
          icon={Activity}
          gradient="emerald"
        />
        <NeonMetricCard
          title="Teams"
          value={analytics?.summary?.teams?.length || 0}
          subtitle={analytics?.summary?.teams?.join(', ') || 'No teams'}
          icon={Users}
          gradient="violet"
        />
        <NeonMetricCard
          title="Within Threshold"
          value={analytics?.capacity_outcomes?.['Within Threshold'] || 0}
          subtitle="Optimal capacity"
          icon={CheckCircle2}
          gradient="emerald"
          trend="up"
          trendValue="On target"
        />
        <NeonMetricCard
          title="Needs Attention"
          value={(analytics?.capacity_outcomes?.['Under Capacity'] || 0) + (analytics?.capacity_outcomes?.['Over Capacity'] || 0)}
          subtitle="Under/Over capacity"
          icon={AlertTriangle}
          gradient="amber"
        />
      </div>

      {/* Team Breakdown - Futuristic Cards */}
      {analytics?.team_breakdown && Object.keys(analytics.team_breakdown).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analytics.team_breakdown).map(([team, data]) => {
            const teamColor = getTeamColor(team);
            return (
              <NeonCard 
                key={team} 
                glowColor={team === 'Blue' ? 'cyan' : team === 'Green' ? 'emerald' : 'amber'}
                className="overflow-hidden group"
              >
                <CardContent className="pt-5 relative">
                  {/* Animated gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${teamColor.bg} opacity-10 group-hover:opacity-20 transition-opacity`} />
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2">
                        <StatusPulse status="active" />
                        <TeamBadge team={team} />
                      </div>
                      <span className="font-bold text-white text-lg">{team}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-slate-800/50 rounded-xl p-3 backdrop-blur-sm">
                        <p className="text-slate-400 text-xs uppercase tracking-wide">Staff</p>
                        <p className="font-bold text-2xl text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>{data.unique_clinicians}</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-3 backdrop-blur-sm">
                        <p className="text-slate-400 text-xs uppercase tracking-wide">Avg Visits</p>
                        <p className="font-bold text-2xl text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>{data.avg_visits}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </NeonCard>
            );
          })}
        </div>
      )}

      {/* Futuristic Tabs */}
      <Tabs defaultValue="productivity" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-2 p-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50">
          <TabsTrigger value="productivity" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/30 rounded-xl px-4 py-2 transition-all">
            Staffing Productivity
          </TabsTrigger>
          <TabsTrigger value="capacity" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/30 rounded-xl px-4 py-2 transition-all">
            RN Capacity
          </TabsTrigger>
          <TabsTrigger value="discipline" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-violet-500/30 rounded-xl px-4 py-2 transition-all">
            Discipline Breakdown
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-amber-500/30 rounded-xl px-4 py-2 transition-all">
            <TrendingUp className="h-4 w-4 mr-2" />
            Historical Trends
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-rose-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-pink-500/30 rounded-xl px-4 py-2 transition-all">
            Analytics
          </TabsTrigger>
          <TabsTrigger value="ai-insights" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-fuchsia-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-fuchsia-500/30 rounded-xl px-4 py-2 transition-all">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Insights
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-slate-500 data-[state=active]:to-slate-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-xl px-4 py-2 transition-all">
            <Clock className="h-4 w-4 mr-2" />
            Data History
          </TabsTrigger>
          <TabsTrigger value="staff-roster" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-teal-500/30 rounded-xl px-4 py-2 transition-all">
            <Users className="h-4 w-4 mr-2" />
            Staff Roster
          </TabsTrigger>
          <TabsTrigger value="schedule-intel" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-orange-500/30 rounded-xl px-4 py-2 transition-all">
            <FileUp className="h-4 w-4 mr-2" />
            Schedule Intelligence
          </TabsTrigger>
        </TabsList>

        {/* Productivity Tab */}
        <TabsContent value="productivity" className="space-y-4">
          <ProductivityTab
            clinicians={clinicians}
            canEdit={canEdit}
            onAddEntry={() => setShowAddDialog(true)}
            onNewWeek={() => openNewWeekDialog('productivity')}
            onBulkDelete={() => openBulkDeleteDialog('productivity')}
            onEdit={(c) => openEditDialog(c, 'productivity')}
            onDelete={(c) => confirmDelete(c, 'productivity')}
            initialPeriod={selectedProductivityPeriod}
            onPeriodChange={setSelectedProductivityPeriod}
          />
        </TabsContent>

        {/* Capacity Tab */}
        <TabsContent value="capacity" className="space-y-6">
          {/* Caseload Section - Expected vs Actual with Burnout Integration */}
          <CaseloadSection 
            token={token} 
            period={selectedCapacityPeriod}
            canEdit={canEdit}
            onEditCapacity={(c) => openEditDialog(c, 'capacity')}
            onDeleteCapacity={(c) => confirmDelete(c, 'capacity')}
          />
          
          {/* Visit Capacity Table */}
          <CapacityTab
            capacityData={capacityData}
            canEdit={canEdit}
            onAddCapacity={() => setShowCapacityDialog(true)}
            onNewWeek={() => openNewWeekDialog('capacity')}
            onBulkDelete={() => openBulkDeleteDialog('capacity')}
            onEdit={(c) => openEditDialog(c, 'capacity')}
            onDelete={(c) => confirmDelete(c, 'capacity')}
            initialPeriod={selectedCapacityPeriod}
            onPeriodChange={setSelectedCapacityPeriod}
          />
        </TabsContent>

        {/* Discipline Breakdown Tab */}
        <TabsContent value="discipline" className="space-y-4">
          <DisciplineTab
            disciplineSummaries={disciplineSummaries}
            canEdit={canEdit}
            onAddSummary={() => setShowDisciplineDialog(true)}
            onDelete={(s) => confirmDelete(s, 'discipline')}
          />
        </TabsContent>

        {/* Historical Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <HistoricalTrendsTab
            disciplineSummaries={disciplineSummaries}
            capacityData={capacityData}
          />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {/* Team Trend Charts */}
          <TeamTrendChart token={token} />
          
          {/* Clinician Productivity Table */}
          <ClinicianProductivityTable token={token} />
          
          {/* Existing Analytics */}
          <AnalyticsTab
            outcomeData={outcomeData}
            capacityOutcomeData={capacityOutcomeData}
            disciplineSummaries={disciplineSummaries}
          />
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai-insights" className="space-y-4">
          <AIInsightsTab
            aiAnalysis={aiAnalysis}
            analyzingAI={analyzingAI}
            onRunAnalysis={runAIAnalysis}
          />
        </TabsContent>

        {/* Data History Tab */}
        <TabsContent value="history" className="space-y-4">
          <NeonCard glowColor="cyan" className="p-6">
            <NeonSectionHeader 
              icon={Clock} 
              title="Staff Productivity History" 
              subtitle="View timeline of data entries and audit logs"
            />
            <DataHistoryPanel 
              category="workforce" 
              title="Staff Productivity Data" 
              defaultExpanded={true}
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
          </NeonCard>
        </TabsContent>

        {/* Staff Roster Tab */}
        <TabsContent value="staff-roster" className="space-y-4" data-testid="staff-roster-tab">
          <NeonCard glowColor="teal" className="p-6">
            <div className="flex items-center justify-between mb-4">
              <NeonSectionHeader 
                icon={Users} 
                title="Staff Roster Management" 
                subtitle="Delete staff no longer in the organization or update team assignments"
              />
              <Button onClick={fetchStaffRoster} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="refresh-roster-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${staffLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {staffLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
              </div>
            ) : staffRoster.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Discipline</TableHead>
                    <TableHead className="text-slate-400">Team</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Source</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffRoster.map((staff, idx) => (
                    <TableRow key={staff.id || staff.name || idx} className="border-slate-800 hover:bg-slate-800/50" data-testid={`staff-row-${idx}`}>
                      <TableCell className="text-white font-medium">{staff.name}</TableCell>
                      <TableCell>
                        <Badge className="bg-cyan-500/20 text-cyan-400">{staff.discipline || staff.role || 'RN'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          (staff.team || '').toLowerCase().includes('blue') ? 'bg-blue-500/20 text-blue-400' :
                          (staff.team || '').toLowerCase().includes('green') ? 'bg-emerald-500/20 text-emerald-400' :
                          (staff.team || '').toLowerCase().includes('yellow') ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-500/20 text-slate-400'
                        }>
                          {staff.team || 'Unassigned'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={staff.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                          {staff.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs">{staff.source || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openStaffEdit(staff)} className="h-8 px-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" data-testid={`edit-staff-${idx}`}>
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => confirmStaffDelete(staff)} className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-staff-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No staff loaded. Click Refresh to load roster.</p>
              </div>
            )}
          </NeonCard>
        </TabsContent>

        {/* Schedule Intelligence Tab */}
        <TabsContent value="schedule-intel" className="space-y-4" data-testid="schedule-intel-tab">
          <NeonCard glowColor="orange" className="p-6">
            <NeonSectionHeader 
              icon={FileUp} 
              title="Schedule Intelligence" 
              subtitle="Upload Consolo Field Staff Schedule to analyze RN overload and PRN demand"
            />

            {/* Upload + PTO Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              {/* File Upload */}
              <div className="lg:col-span-2 space-y-3">
                <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  scheduleFile ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600 hover:border-orange-500/50 hover:bg-slate-800/50'
                }`} onClick={() => document.getElementById('schedule-file-input')?.click()} data-testid="schedule-drop-zone">
                  <input id="schedule-file-input" type="file" className="hidden" accept=".csv,.xlsx,.xls"
                    onChange={(e) => e.target.files?.[0] && setScheduleFile(e.target.files[0])} />
                  {scheduleFile ? (
                    <div>
                      <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                      <p className="text-white font-medium">{scheduleFile.name}</p>
                      <p className="text-sm text-slate-400">{(scheduleFile.size / 1024).toFixed(1)} KB</p>
                      <Button variant="ghost" size="sm" className="mt-2 text-slate-400 hover:text-red-400"
                        onClick={(e) => { e.stopPropagation(); setScheduleFile(null); setScheduleAnalysis(null); }}>
                        <XCircle className="h-4 w-4 mr-1" />Remove
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-10 w-10 text-slate-500 mx-auto mb-2" />
                      <p className="text-white">Drop Consolo Schedule CSV/Excel here</p>
                      <p className="text-xs text-slate-500 mt-1">Columns: Date | Patient | Status | Role | Office | Completed by | Assigned to</p>
                    </div>
                  )}
                </div>

                <Button onClick={analyzeSchedule} disabled={!scheduleFile || scheduleLoading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 h-11" data-testid="analyze-schedule-btn">
                  {scheduleLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</> : <><BarChart3 className="h-4 w-4 mr-2" />Analyze Schedule</>}
                </Button>
              </div>

              {/* PTO Input */}
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> PTO Entries (Optional)
                  </h4>
                  <div className="space-y-2">
                    <Input placeholder="RN Name" value={ptoName} onChange={(e) => setPtoName(e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white text-sm" data-testid="pto-name" />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">From</label>
                        <Input type="date" value={ptoDate} onChange={(e) => setPtoDate(e.target.value)}
                          className="bg-slate-900 border-slate-600 text-white text-sm" data-testid="pto-date" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">To</label>
                        <Input type="date" value={ptoDateEnd} onChange={(e) => setPtoDateEnd(e.target.value)}
                          className="bg-slate-900 border-slate-600 text-white text-sm" data-testid="pto-date-end" />
                      </div>
                    </div>
                    <Button onClick={addPtoEntry} size="sm" variant="outline" className="w-full border-slate-600 text-slate-300" data-testid="add-pto-btn">
                      <Plus className="h-3 w-3 mr-1" /> Add PTO
                    </Button>
                  </div>
                  {ptoEntries.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {ptoEntries.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-slate-900">
                          <span className="text-slate-300">{p.rn_name} - {p.date}</span>
                          <button onClick={() => setPtoEntries(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300">
                            <XCircle className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* IDG Meeting */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" /> IDG Meeting (Optional)
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">Nurses with IDG this week get capacity reduced to 18</p>
                  <div className="space-y-2">
                    <Input placeholder="RN Name" value={idgName} onChange={(e) => setIdgName(e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white text-sm" data-testid="idg-name" />
                    <Button onClick={() => {
                      if (!idgName.trim()) { toast.error('Enter RN name'); return; }
                      setIdgNurses(prev => [...prev, idgName.trim()]);
                      setIdgName('');
                    }} size="sm" variant="outline" className="w-full border-slate-600 text-slate-300" data-testid="add-idg-btn">
                      <Plus className="h-3 w-3 mr-1" /> Add IDG Nurse
                    </Button>
                  </div>
                  {idgNurses.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {idgNurses.map((name, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-slate-900">
                          <span className="text-purple-300">{name} — cap: 18/week</span>
                          <button onClick={() => setIdgNurses(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300">
                            <XCircle className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Unplanned Visits */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> Unplanned Visits (Optional)
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">Add expected unplanned visits per day. Defaults: Tue/Thu/Fri=3, Wed=2, Mon=1</p>
                  <div className="space-y-2">
                    <Input type="date" value={unplannedDate} onChange={(e) => setUnplannedDate(e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white text-sm" data-testid="unplanned-date" />
                    <Input type="number" placeholder="# of visits" value={unplannedCount} onChange={(e) => setUnplannedCount(e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white text-sm" min="0" data-testid="unplanned-count" />
                    <Button onClick={() => {
                      if (!unplannedDate || !unplannedCount) { toast.error('Enter date and count'); return; }
                      setUnplannedVisits(prev => ({ ...prev, [unplannedDate]: parseInt(unplannedCount) || 0 }));
                      setUnplannedDate(''); setUnplannedCount('');
                    }} size="sm" variant="outline" className="w-full border-slate-600 text-slate-300" data-testid="add-unplanned-btn">
                      <Plus className="h-3 w-3 mr-1" /> Add Unplanned
                    </Button>
                  </div>
                  {Object.keys(unplannedVisits).length > 0 && (
                    <div className="mt-3 space-y-1">
                      {Object.entries(unplannedVisits).sort().map(([date, count]) => (
                        <div key={date} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-slate-900">
                          <span className="text-red-300">{date}: {count} unplanned visits</span>
                          <button onClick={() => setUnplannedVisits(prev => { const n = {...prev}; delete n[date]; return n; })} className="text-red-400 hover:text-red-300">
                            <XCircle className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </NeonCard>

          {/* Analysis Results */}
          {scheduleAnalysis && (
            <>
              {/* Summary Cards + Export */}
              <div className="flex items-center justify-between mb-2">
                <div />
                <Button onClick={exportSchedulePDF} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500" data-testid="export-schedule-pdf-btn">
                  <FileDown className="h-4 w-4 mr-2" />
                  Export Calendar PDF
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <NeonMetricCard icon={Users} label="TOTAL RNs" value={scheduleAnalysis.weekly_totals?.total_rns || 0} />
                <NeonMetricCard icon={Calendar} label="DAYS" value={scheduleAnalysis.weekly_totals?.total_days || 0} />
                <NeonMetricCard icon={Activity} label="TOTAL VISITS" value={scheduleAnalysis.weekly_totals?.total_visits || 0} />
                <NeonMetricCard icon={AlertTriangle} label="OVERLOAD FLAGS" value={scheduleAnalysis.flags?.length || 0} />
                <NeonMetricCard icon={UserCheck} label="PTO VISITS" value={scheduleAnalysis.weekly_totals?.total_pto_visits || 0} />
              </div>

              {/* Schedule Balance Alerts — same nurse, different days */}
              {scheduleAnalysis.distribution_alerts?.length > 0 && (
                <NeonCard glowColor="amber" className="p-6">
                  <NeonSectionHeader icon={AlertTriangle} title="Schedule Balance Alerts" subtitle="Nurses with heavy days who also have lighter days — manager follow-up recommended" />
                  <div className="space-y-2 mt-3">
                    {scheduleAnalysis.distribution_alerts.map((alert, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${alert.can_fix_fully ? 'bg-amber-500/10 border-amber-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-medium">{alert.rn}</span>
                          <Badge className={alert.can_fix_fully ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                            {alert.can_fix_fully ? 'Fixable' : 'Partial'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-300">
                          <span className="text-red-400 font-semibold">{alert.heavy_visits} visits</span> on <span className="text-white">{alert.heavy_date}</span>
                          <span className="text-slate-500"> (over by {alert.over_by}, threshold: {alert.threshold})</span>
                        </p>
                        <p className="text-xs text-amber-400 mt-1">
                          Could move to: {alert.light_days?.map(d => `${d.date} (${d.current_visits} visits, ${d.available_slots} open slots)`).join(' | ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </NeonCard>
              )}

              {/* Excluded Staff Notice */}
              {scheduleAnalysis.excluded_staff?.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700">
                  <Users className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-400">
                    Excluded from analysis: <span className="text-slate-300">{scheduleAnalysis.excluded_staff.join(', ')}</span>
                    <span className="text-slate-500 ml-1">({scheduleAnalysis.weekly_totals?.excluded_visits || 0} visits)</span>
                  </span>
                </div>
              )}

              {/* Underutilized Nurses (≤3 visits/day) */}
              {scheduleAnalysis.underutilized_alerts?.length > 0 && (
                <NeonCard glowColor="cyan" className="p-6">
                  <NeonSectionHeader icon={UserCheck} title="Underutilized Nurses (3 or fewer visits)" subtitle="Manager review — only ask to support if weekly capacity < 16" />
                  <Table className="mt-3">
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">RN</TableHead>
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400 text-center">Visits</TableHead>
                        <TableHead className="text-slate-400 text-center">Open Slots</TableHead>
                        <TableHead className="text-slate-400 text-center">Weekly Total</TableHead>
                        <TableHead className="text-slate-400 text-center">Can Support?</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduleAnalysis.underutilized_alerts.map((u, i) => (
                        <TableRow key={i} className="border-slate-800">
                          <TableCell className="text-white font-medium">{u.rn}</TableCell>
                          <TableCell className="text-slate-300">{u.date}</TableCell>
                          <TableCell className="text-center"><Badge className="bg-cyan-500/20 text-cyan-400">{u.visits}</Badge></TableCell>
                          <TableCell className="text-center text-emerald-400">{u.available_slots}</TableCell>
                          <TableCell className="text-center text-slate-300">{u.weekly_total}/{u.weekly_capacity}</TableCell>
                          <TableCell className="text-center">
                            {u.can_support_others ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400">Yes (&lt;16)</Badge>
                            ) : (
                              <Badge className="bg-slate-500/20 text-slate-400">Review only</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </NeonCard>
              )}

              {/* Flags Table */}
              {scheduleAnalysis.flags?.length > 0 && (
                <NeonCard glowColor="red" className="p-6">
                  <NeonSectionHeader icon={AlertTriangle} title="Overloaded RNs (above threshold)" subtitle={`${scheduleAnalysis.flags.length} flags found`} />
                  <Table className="mt-3">
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">RN</TableHead>
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400 text-center">Threshold</TableHead>
                        <TableHead className="text-slate-400 text-center">Visits</TableHead>
                        <TableHead className="text-slate-400 text-center">Over By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduleAnalysis.flags.map((f, i) => (
                        <TableRow key={i} className="border-slate-800">
                          <TableCell className="text-white font-medium">{f.rn}</TableCell>
                          <TableCell className="text-slate-300">{f.date}</TableCell>
                          <TableCell className="text-center"><Badge className="bg-slate-500/20 text-slate-300">{f.threshold}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-red-500/20 text-red-400">{f.visits}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-orange-500/20 text-orange-400">+{f.over_by}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {scheduleAnalysis.structural_overload?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {scheduleAnalysis.structural_overload.map((so, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                          <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                          <span className="text-sm text-red-300">STRUCTURAL OVERLOAD: {so.rn} over limit {so.days_over}/{so.total_days} days ({so.pct_over}%) — panel restructuring recommended</span>
                        </div>
                      ))}
                    </div>
                  )}
                </NeonCard>
              )}

              {/* Per-RN Table */}
              <NeonCard glowColor="cyan" className="p-6">
                <NeonSectionHeader icon={Users} title="Per-RN Capacity Analysis" subtitle="Weekly capacity = min(4 x days worked, 20)" />
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">RN</TableHead>
                      <TableHead className="text-slate-400 text-center">Days Worked</TableHead>
                      <TableHead className="text-slate-400 text-center">Weekly Capacity</TableHead>
                      <TableHead className="text-slate-400 text-center">Visits Scheduled</TableHead>
                      <TableHead className="text-slate-400 text-center">Overflow</TableHead>
                      <TableHead className="text-slate-400 text-center">PTO Days</TableHead>
                      <TableHead className="text-slate-400 text-center">PTO Visits</TableHead>
                      <TableHead className="text-slate-400 text-center">Utilization</TableHead>
                      <TableHead className="text-slate-400 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleAnalysis.per_rn?.map((r, i) => (
                      <TableRow key={i} className={`border-slate-800 ${
                        r.status === 'pto' ? 'bg-amber-500/5' :
                        r.status === 'over_weekly' ? 'bg-red-500/5' : ''
                      }`}>
                        <TableCell className="text-white font-medium">
                          {r.rn}
                          {r.has_idg && <Badge className="ml-2 bg-purple-500/20 text-purple-400 text-xs">IDG</Badge>}
                        </TableCell>
                        <TableCell className="text-center text-slate-300">{r.days_worked}</TableCell>
                        <TableCell className="text-center text-slate-300">{r.weekly_capacity}</TableCell>
                        <TableCell className="text-center text-white font-semibold">{r.visits_scheduled}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={r.overflow > 0 ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}>
                            {r.overflow > 0 ? `+${r.overflow}` : '0'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {r.pto_days > 0 ? <Badge className="bg-amber-500/20 text-amber-400">{r.pto_days} days</Badge> : <span className="text-slate-500">-</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          {r.pto_visits_to_cover > 0 ? <Badge className="bg-amber-500/20 text-amber-400">{r.pto_visits_to_cover}</Badge> : <span className="text-slate-500">-</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          {r.status !== 'pto' ? (
                            <Badge className={r.utilization_pct > 100 ? 'bg-red-500/20 text-red-400' : r.utilization_pct > 80 ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}>
                              {r.utilization_pct}%
                            </Badge>
                          ) : <span className="text-amber-400">PTO</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={
                            r.status === 'pto' ? 'bg-amber-500/30 text-amber-300 font-bold' :
                            r.status === 'partial_pto' ? 'bg-amber-500/20 text-amber-400' :
                            r.status === 'over_weekly' ? 'bg-red-500/20 text-red-400 font-bold' :
                            'bg-emerald-500/20 text-emerald-400'
                          }>
                            {r.status === 'pto' ? 'ON PTO' :
                             r.status === 'partial_pto' ? 'Partial PTO' :
                             r.status === 'over_weekly' ? 'OVER' : 'OK'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </NeonCard>

              {/* Per-Day Table */}
              <NeonCard glowColor="amber" className="p-6">
                <NeonSectionHeader icon={Calendar} title="Per-Day PRN Coverage" subtitle="Daily overflow + PTO = PRN visits needed" />
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400 text-center">RNs Working</TableHead>
                      <TableHead className="text-slate-400 text-center">Total Visits</TableHead>
                      <TableHead className="text-slate-400 text-center">Overflow</TableHead>
                      <TableHead className="text-slate-400 text-center">PTO</TableHead>
                      <TableHead className="text-slate-400 text-center">Unplanned</TableHead>
                      <TableHead className="text-slate-400 text-center">PRN Visits</TableHead>
                      <TableHead className="text-slate-400 text-center">PRN Shifts</TableHead>
                      <TableHead className="text-slate-400 text-center">PRN Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleAnalysis.per_day?.map((d, i) => (
                      <TableRow key={i} className="border-slate-800">
                        <TableCell className="text-white font-medium">{d.date}</TableCell>
                        <TableCell className="text-center text-slate-300">{d.rns_working}</TableCell>
                        <TableCell className="text-center text-white">{d.total_visits}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.overflow_visits > 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-500/20 text-slate-400'}>{d.overflow_visits}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.pto_visits > 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'}>{d.pto_visits}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.unplanned_visits > 0 ? 'bg-red-500/20 text-red-300' : 'bg-slate-500/20 text-slate-400'}>{d.unplanned_visits || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.prn_visits_needed > 0 ? 'bg-red-500/20 text-red-400 font-bold' : 'bg-emerald-500/20 text-emerald-400'}>{d.net_prn_gap || d.prn_visits_needed}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.prn_shifts > 0 ? 'bg-red-500/20 text-red-400 font-bold' : 'bg-emerald-500/20 text-emerald-400'}>{d.prn_shifts || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.prn_hours > 0 ? 'bg-orange-500/20 text-orange-400 font-bold' : 'bg-emerald-500/20 text-emerald-400'}>{d.prn_hours || 0}h</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </NeonCard>

              {/* Weekly Totals + Notes */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <NeonCard glowColor="purple" className="p-6">
                  <NeonSectionHeader icon={BarChart3} title="Weekly PRN Summary" />
                  <div className="space-y-3 mt-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Total RN Overflow Visits</span>
                      <span className="text-white font-bold">{scheduleAnalysis.weekly_totals?.total_rn_overflow || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Total PTO Visits to Cover</span>
                      <span className="text-white font-bold">{scheduleAnalysis.weekly_totals?.total_pto_visits || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Unplanned Visits (Tue/Thu/Fri heavy)</span>
                      <span className="text-white font-bold">{scheduleAnalysis.weekly_totals?.total_unplanned_visits || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400 font-medium">Total PRN Visit Demand</span>
                      <span className="text-orange-400 font-black text-lg">{scheduleAnalysis.weekly_totals?.total_prn_visit_demand || 0} visits</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400 font-medium">Total PRN Shifts (4 visits = 1 shift)</span>
                      <span className="text-red-400 font-black text-lg">{scheduleAnalysis.weekly_totals?.total_prn_shifts || 0} shifts</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400 font-medium">Total PRN Hours (8 hrs/shift)</span>
                      <span className="text-red-400 font-black text-lg">{scheduleAnalysis.weekly_totals?.total_prn_hours || 0} hours</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400 font-medium">Full-Time PRNs Needed (5 shifts/wk)</span>
                      <span className="text-red-400 font-black text-2xl">{scheduleAnalysis.weekly_totals?.weekly_prns_needed || 0}</span>
                    </div>
                    {scheduleAnalysis.weekly_totals?.peak_day?.date && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-slate-400">Peak Day</span>
                        <span className="text-amber-400">{scheduleAnalysis.weekly_totals.peak_day.date} ({scheduleAnalysis.weekly_totals.peak_day.prn_visits} visits, {scheduleAnalysis.weekly_totals.peak_day.prn_shifts} shifts, {scheduleAnalysis.weekly_totals.peak_day.prn_hours}h)</span>
                      </div>
                    )}
                  </div>
                </NeonCard>

                <NeonCard glowColor="slate" className="p-6">
                  <NeonSectionHeader icon={FileSpreadsheet} title="Assumptions & Notes" />
                  <div className="space-y-2 mt-3">
                    {scheduleAnalysis.assumptions?.map((a, i) => (
                      <div key={i} className="text-xs text-slate-400 flex items-start gap-2">
                        <span className="text-slate-600 mt-0.5">-</span> {a}
                      </div>
                    ))}
                    {scheduleAnalysis.notes?.map((n, i) => (
                      <div key={i} className={`text-sm px-3 py-2 rounded-lg mt-2 ${
                        n.includes('STRUCTURAL') ? 'bg-red-500/10 border border-red-500/20 text-red-300' : 'bg-slate-800 text-slate-300'
                      }`}>
                        {n}
                      </div>
                    ))}
                  </div>
                </NeonCard>
              </div>
            </>
          )}

          {/* Schedule History */}
          <NeonCard glowColor="slate" className="p-6">
            <div className="flex items-center justify-between mb-4">
              <NeonSectionHeader icon={Clock} title="Analysis History" subtitle="Past schedule analyses" />
              <Button onClick={fetchScheduleHistory} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="refresh-schedule-history">
                <RefreshCw className={`h-4 w-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : scheduleHistory.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">File</TableHead>
                    <TableHead className="text-slate-400 text-center">Week</TableHead>
                    <TableHead className="text-slate-400 text-center">RNs</TableHead>
                    <TableHead className="text-slate-400 text-center">Visits</TableHead>
                    <TableHead className="text-slate-400 text-center">Flags</TableHead>
                    <TableHead className="text-slate-400 text-center">PRN Shifts</TableHead>
                    <TableHead className="text-slate-400 text-center">PRN Hours</TableHead>
                    <TableHead className="text-slate-400 text-center">PRNs</TableHead>
                    <TableHead className="text-slate-400 text-center">PTO</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduleHistory.map((h, i) => (
                    <TableRow key={h.id || i} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell className="text-slate-300 text-xs">{new Date(h.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-white font-medium text-sm truncate max-w-[150px]">{h.filename}</TableCell>
                      <TableCell className="text-center text-xs text-slate-400">{h.date_range?.start?.slice(5)} – {h.date_range?.end?.slice(5)}</TableCell>
                      <TableCell className="text-center text-slate-300">{h.total_rns}</TableCell>
                      <TableCell className="text-center text-white font-semibold">{h.total_visits}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={h.flags_count > 0 ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}>{h.flags_count}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={h.total_prn_shifts > 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-emerald-500/20 text-emerald-400'}>{h.total_prn_shifts || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={h.total_prn_hours > 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-emerald-500/20 text-emerald-400'}>{h.total_prn_hours || 0}h</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={h.prns_needed > 0 ? 'bg-red-500/20 text-red-400 font-bold' : 'bg-emerald-500/20 text-emerald-400'}>{h.prns_needed}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {h.pto_count > 0 ? <Badge className="bg-amber-500/20 text-amber-400">{h.pto_count}</Badge> : <span className="text-slate-600">-</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => deleteScheduleAnalysis(h.id)} className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No schedule analyses yet</p>
                <p className="text-xs mt-1">Upload a Consolo schedule to get started</p>
              </div>
            )}
          </NeonCard>
        </TabsContent>

        {/* Staff Edit Dialog */}
        <Dialog open={showStaffEditDialog} onOpenChange={setShowStaffEditDialog}>
          <DialogContent className="bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Pencil className="h-5 w-5 text-cyan-400" />
                Edit Staff: {staffEditTarget?.name}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Changes will sync across roster, productivity, and capacity records.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-slate-400">Team</Label>
                <Select value={staffEditData.team} onValueChange={(v) => setStaffEditData(prev => ({ ...prev, team: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="staff-team-select">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="Blue">Blue</SelectItem>
                    <SelectItem value="Green">Green</SelectItem>
                    <SelectItem value="Yellow">Yellow</SelectItem>
                    <SelectItem value="BLUE">BLUE</SelectItem>
                    <SelectItem value="GREEN">GREEN</SelectItem>
                    <SelectItem value="YELLOW">YELLOW</SelectItem>
                    <SelectItem value="Unassigned">Unassigned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400">Discipline</Label>
                <Select value={staffEditData.discipline} onValueChange={(v) => setStaffEditData(prev => ({ ...prev, discipline: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="staff-discipline-select">
                    <SelectValue placeholder="Select discipline" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="RN">RN</SelectItem>
                    <SelectItem value="RNCM">RNCM</SelectItem>
                    <SelectItem value="LPN">LPN</SelectItem>
                    <SelectItem value="Aide">Aide</SelectItem>
                    <SelectItem value="SW">Social Worker</SelectItem>
                    <SelectItem value="Chaplain">Chaplain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStaffEditDialog(false)} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button onClick={saveStaffEdit} disabled={staffActionLoading} className="bg-gradient-to-r from-cyan-500 to-blue-600" data-testid="save-staff-edit-btn">
                {staffActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Staff Delete Confirmation */}
        <Dialog open={showStaffDeleteDialog} onOpenChange={setShowStaffDeleteDialog}>
          <DialogContent className="bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-400" />
                Remove Staff Member
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                This will permanently delete <strong className="text-white">{staffDeleteTarget?.name}</strong> from the roster, all capacity records, and all productivity records. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStaffDeleteDialog(false)} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button onClick={handleStaffDelete} disabled={staffActionLoading} className="bg-red-600 hover:bg-red-700" data-testid="confirm-staff-delete-btn">
                {staffActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Tabs>

      {/* Paste Data Dialog */}
      <Dialog open={showPasteDialog} onOpenChange={setShowPasteDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5" />
              Paste Productivity Data
            </DialogTitle>
            <DialogDescription>
              Copy data from Excel or a spreadsheet and paste below. Include headers.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="mb-4 p-3 bg-muted rounded-lg text-sm">
              <p className="font-medium mb-1">Expected format (tab-separated):</p>
              <p className="text-muted-foreground font-mono text-xs">
                CLINICIANS  Hire Date  TEAM  RNCM VISIT CAPACITY  Expectation  Planning Outcome  COMMENT
              </p>
            </div>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste your data here..."
              className="min-h-[200px] font-mono text-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasteDialog(false)}>Cancel</Button>
            <Button onClick={handlePasteData} disabled={processingPaste}>
              {processingPaste ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Import Data'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Clinician Productivity Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Clinician Productivity</DialogTitle>
            <DialogDescription>Enter individual clinician visit data</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Clinician Name</Label>
                <Select 
                  value={newClinicianData.clinician_name} 
                  onValueChange={(v) => handleClinicianSelect(v, 'productivity')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select or type clinician" />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicianList.map(c => (
                      <SelectItem key={c.name} value={c.name}>
                        {c.name} {c.team && `(${c.team})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  className="mt-2"
                  value={newClinicianData.clinician_name}
                  onChange={(e) => setNewClinicianData({...newClinicianData, clinician_name: e.target.value})}
                  placeholder="Or type new name"
                />
              </div>
              <div>
                <Label>Hire Date</Label>
                <Input 
                  type="date"
                  value={newClinicianData.hire_date}
                  onChange={(e) => setNewClinicianData({...newClinicianData, hire_date: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Team</Label>
                <Select value={newClinicianData.team} onValueChange={(v) => setNewClinicianData({...newClinicianData, team: v})}>
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {TEAMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discipline</Label>
                <Select value={newClinicianData.discipline} onValueChange={(v) => setNewClinicianData({...newClinicianData, discipline: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input 
                  type="date"
                  value={newClinicianData.period_start}
                  onChange={(e) => setNewClinicianData({...newClinicianData, period_start: e.target.value})}
                />
              </div>
              <div>
                <Label>Period End</Label>
                <Input 
                  type="date"
                  value={newClinicianData.period_end}
                  onChange={(e) => setNewClinicianData({...newClinicianData, period_end: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Visits</Label>
                <Input 
                  type="number"
                  value={newClinicianData.total_visits}
                  onChange={(e) => setNewClinicianData({...newClinicianData, total_visits: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Avg Time per Visit (min)</Label>
                <Input 
                  type="number"
                  value={newClinicianData.average_time_per_visit}
                  onChange={(e) => setNewClinicianData({...newClinicianData, average_time_per_visit: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expectation Min</Label>
                <Input 
                  type="number"
                  value={newClinicianData.expectation_min}
                  onChange={(e) => setNewClinicianData({...newClinicianData, expectation_min: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Expectation Max</Label>
                <Input 
                  type="number"
                  value={newClinicianData.expectation_max}
                  onChange={(e) => setNewClinicianData({...newClinicianData, expectation_max: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <Label>Comment</Label>
              <Input 
                value={newClinicianData.comment}
                onChange={(e) => setNewClinicianData({...newClinicianData, comment: e.target.value})}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddClinician}>Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Capacity Dialog */}
      <Dialog open={showCapacityDialog} onOpenChange={setShowCapacityDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add RNCM Capacity</DialogTitle>
            <DialogDescription>Enter clinician visit capacity data</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Period Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input 
                  type="date"
                  value={newCapacityData.period_start}
                  onChange={(e) => setNewCapacityData({...newCapacityData, period_start: e.target.value})}
                />
              </div>
              <div>
                <Label>Period End</Label>
                <Input 
                  type="date"
                  value={newCapacityData.period_end}
                  onChange={(e) => setNewCapacityData({...newCapacityData, period_end: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Clinician Name</Label>
                <Select 
                  value={newCapacityData.clinician_name} 
                  onValueChange={(v) => handleClinicianSelect(v, 'capacity')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select clinician" />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicianList.map(c => (
                      <SelectItem key={c.name} value={c.name}>
                        {c.name} {c.team && `(${c.team})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  className="mt-2"
                  value={newCapacityData.clinician_name}
                  onChange={(e) => setNewCapacityData({...newCapacityData, clinician_name: e.target.value})}
                  placeholder="Or type new name"
                />
              </div>
              <div>
                <Label>Hire Date</Label>
                <Input 
                  type="date"
                  value={newCapacityData.hire_date}
                  onChange={(e) => setNewCapacityData({...newCapacityData, hire_date: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Team</Label>
                <Select value={newCapacityData.team} onValueChange={(v) => setNewCapacityData({...newCapacityData, team: v})}>
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {TEAMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visit Capacity</Label>
                <Input 
                  type="number"
                  value={newCapacityData.visit_capacity}
                  onChange={(e) => setNewCapacityData({...newCapacityData, visit_capacity: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expectation Min</Label>
                <Input 
                  type="number"
                  value={newCapacityData.expectation_min}
                  onChange={(e) => setNewCapacityData({...newCapacityData, expectation_min: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Expectation Max</Label>
                <Input 
                  type="number"
                  value={newCapacityData.expectation_max}
                  onChange={(e) => setNewCapacityData({...newCapacityData, expectation_max: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <Label>Comment</Label>
              <Input 
                value={newCapacityData.comment}
                onChange={(e) => setNewCapacityData({...newCapacityData, comment: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCapacityDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCapacity}>Add Capacity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Discipline Summary Dialog */}
      <Dialog open={showDisciplineDialog} onOpenChange={setShowDisciplineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Discipline Summary</DialogTitle>
            <DialogDescription>Enter weekly visit totals by discipline</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input 
                  type="date"
                  value={newDisciplineData.period_start}
                  onChange={(e) => setNewDisciplineData({...newDisciplineData, period_start: e.target.value})}
                />
              </div>
              <div>
                <Label>Period End</Label>
                <Input 
                  type="date"
                  value={newDisciplineData.period_end}
                  onChange={(e) => setNewDisciplineData({...newDisciplineData, period_end: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>RNCMs Visits</Label>
                <Input 
                  type="number"
                  value={newDisciplineData.rncm_visits}
                  onChange={(e) => setNewDisciplineData({...newDisciplineData, rncm_visits: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>LPNs Visits</Label>
                <Input 
                  type="number"
                  value={newDisciplineData.lpn_visits}
                  onChange={(e) => setNewDisciplineData({...newDisciplineData, lpn_visits: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Wound RN Visits</Label>
                <Input 
                  type="number"
                  value={newDisciplineData.wound_rn_visits}
                  onChange={(e) => setNewDisciplineData({...newDisciplineData, wound_rn_visits: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>PRNs (Weekday) Visits</Label>
                <Input 
                  type="number"
                  value={newDisciplineData.prn_visits}
                  onChange={(e) => setNewDisciplineData({...newDisciplineData, prn_visits: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisciplineDialog(false)}>Cancel</Button>
            <Button onClick={handleAddDisciplineSummary}>Add Summary</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog - Enhanced with AI Analysis */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { setShowUploadDialog(open); if (!open) resetUploadAnalysis(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#00b0ab]" />
              Upload & Analyze RNCM Productivity Data
            </DialogTitle>
            <DialogDescription>
              Upload productivity data to extract metrics, get AI insights, and identify PIP opportunities
            </DialogDescription>
          </DialogHeader>
          
          {!uploadAnalysisResult ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Period Start Date</Label>
                  <Input 
                    type="date" 
                    value={uploadPeriod}
                    onChange={(e) => setUploadPeriod(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Upload File (CSV, Excel)</Label>
                  <Input 
                    type="file" 
                    accept=".csv,.xlsx,.xls"
                    ref={uploadFileRef}
                    data-testid="rncm-upload-file"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Or Paste Data</Label>
                <Textarea 
                  placeholder="Paste productivity data here... (e.g., from spreadsheet with columns: Name, Team, Visits, Patients)"
                  value={uploadTextContent}
                  onChange={(e) => setUploadTextContent(e.target.value)}
                  rows={6}
                  data-testid="rncm-upload-text"
                />
              </div>

              <div className="border-2 border-dashed rounded-lg p-4 text-center bg-slate-50 dark:bg-slate-900">
                <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Supported columns: CLINICIANS/NAME, TEAM, DISCIPLINE, ASSIGNED PATIENTS, 
                  VISITS COMPLETED, VISITS SCHEDULED, AVG DAILY VISITS, DOCUMENTATION HOURS
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUploadWithAnalysis} 
                  disabled={uploadingWithAnalysis}
                  className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                  data-testid="rncm-analyze-btn"
                >
                  {uploadingWithAnalysis ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="h-4 w-4 mr-2" />
                  )}
                  {uploadingWithAnalysis ? 'Analyzing...' : 'Upload & Analyze'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Extracted Data Summary */}
              {uploadAnalysisResult.extracted_data && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      Extracted Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="p-2 rounded bg-slate-50 dark:bg-slate-900">
                        <p className="text-xs text-muted-foreground">Records</p>
                        <p className="font-semibold">{uploadAnalysisResult.clinician_records_count || 0}</p>
                      </div>
                      {uploadAnalysisResult.extracted_data.teams_summary && 
                        Object.entries(uploadAnalysisResult.extracted_data.teams_summary).map(([team, stats]) => (
                          <div key={team} className="p-2 rounded bg-slate-50 dark:bg-slate-900">
                            <p className="text-xs text-muted-foreground">{team} Team</p>
                            <p className="font-semibold">{stats.count} staff, {stats.total_visits} visits</p>
                          </div>
                        ))
                      }
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI Analysis */}
              {uploadAnalysisResult.ai_analysis && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-[#00b0ab]" />
                      AI Analysis & Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-64">
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown>{uploadAnalysisResult.ai_analysis}</ReactMarkdown>
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* PIP Recommendations */}
              {uploadAnalysisResult.pip_recommendations && uploadAnalysisResult.pip_recommendations.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4 text-orange-500" />
                      PIP Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {uploadAnalysisResult.pip_recommendations.map((pip, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 rounded-lg border bg-gradient-to-r from-orange-500/5 to-amber-500/5"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{pip.suggested_pip}</span>
                              <Badge className={pip.priority === 'high' ? 'bg-red-500' : pip.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'} variant="secondary">
                                {pip.priority}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{pip.description}</p>
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => handleCreatePIPFromUpload(index)}
                            data-testid={`create-rncm-pip-${index}`}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Create
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={resetUploadAnalysis}>
                  Upload Another
                </Button>
                <Button onClick={() => setShowUploadDialog(false)}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Week Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5" />
              Create New Medicare Week
            </DialogTitle>
            <DialogDescription>
              Copy all staff entries from a previous week to create a new Medicare week (Sunday-Saturday).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Data Type</Label>
              <Select value={duplicateType} onValueChange={setDuplicateType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="productivity">Clinician Productivity</SelectItem>
                  <SelectItem value="capacity">RN Capacity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Source Week (copy from)</Label>
              <Select 
                value={duplicateData.source_period_start} 
                onValueChange={(v) => {
                  // Auto-calculate next Medicare week when source is selected
                  const nextWeek = getNextMedicareWeekFromPeriod(v);
                  setDuplicateData({
                    source_period_start: v,
                    target_period_start: nextWeek.start,
                    target_period_end: nextWeek.end
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source week" />
                </SelectTrigger>
                <SelectContent>
                  {(duplicateType === 'productivity' ? productivityPeriods : capacityPeriods).map(p => (
                    <SelectItem key={p.period_start} value={p.period_start}>
                      {formatMedicareWeek(p.period_start, p.period_end || p.period_start)} ({p.entry_count} entries)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Target Week Display - Auto-calculated */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <Label className="text-blue-700 dark:text-blue-300 font-medium">New Week (Sunday-Saturday)</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="flex-1">
                  <span className="text-xs text-muted-foreground">Start (Sunday)</span>
                  <Input 
                    type="date"
                    value={duplicateData.target_period_start}
                    onChange={(e) => {
                      // Auto-adjust end date to Saturday (+6 days)
                      const startDate = new Date(e.target.value + 'T00:00:00');
                      const endDate = new Date(startDate);
                      endDate.setDate(startDate.getDate() + 6);
                      setDuplicateData({
                        ...duplicateData,
                        target_period_start: e.target.value,
                        target_period_end: endDate.toISOString().split('T')[0]
                      });
                    }}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-xs text-muted-foreground">End (Saturday)</span>
                  <Input 
                    type="date"
                    value={duplicateData.target_period_end}
                    readOnly
                    className="mt-1 bg-gray-100 dark:bg-gray-800"
                  />
                </div>
              </div>
              {duplicateData.target_period_start && (
                <p className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                  {formatMedicareWeek(duplicateData.target_period_start, duplicateData.target_period_end)}
                </p>
              )}
            </div>

            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="text-muted-foreground text-xs space-y-1">
                <li>• Creates a new Medicare week (Sunday-Saturday)</li>
                <li>• All staff entries from source week will be copied</li>
                <li>• Visit counts preserved - update after creation</li>
                <li>• New week appears in the period dropdown after creation</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Cancel</Button>
            <Button onClick={handleDuplicateWeek} disabled={duplicating || !duplicateData.source_period_start}>
              {duplicating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Week...
                </>
              ) : (
                <>
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Create New Week
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete {deleteType.charAt(0).toUpperCase() + deleteType.slice(1)} Entry
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="py-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="font-medium">{deleteTarget.clinician_name || deleteTarget.period_start || 'Entry'}</p>
                {deleteTarget.team && <p className="text-sm text-muted-foreground mt-1">Team: {deleteTarget.team}</p>}
                {deleteTarget.total_visits !== undefined && (
                  <p className="text-sm text-muted-foreground">Total Visits: {deleteTarget.total_visits}</p>
                )}
                {deleteTarget.visit_capacity !== undefined && (
                  <p className="text-sm text-muted-foreground">Visit Capacity: {deleteTarget.visit_capacity}</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-productivity-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-500" />
              Edit {editType === 'capacity' ? 'Capacity' : 'Productivity'} Entry
            </DialogTitle>
            <DialogDescription>
              Update the details for {editTarget?.clinician_name}
            </DialogDescription>
          </DialogHeader>
          
          {editType === 'capacity' ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Visit Capacity</Label>
                  <Input 
                    type="number" 
                    value={editData.visit_capacity || 0}
                    onChange={(e) => setEditData(prev => ({...prev, visit_capacity: parseInt(e.target.value) || 0}))}
                  />
                </div>
                <div>
                  <Label>Team</Label>
                  <Select value={editData.team || ''} onValueChange={(v) => setEditData(prev => ({...prev, team: v}))}>
                    <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                    <SelectContent>
                      {TEAMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Expectation Min</Label>
                  <Input 
                    type="number" 
                    value={editData.expectation_min || 15}
                    onChange={(e) => setEditData(prev => ({...prev, expectation_min: parseInt(e.target.value) || 15}))}
                  />
                </div>
                <div>
                  <Label>Expectation Max</Label>
                  <Input 
                    type="number" 
                    value={editData.expectation_max || 20}
                    onChange={(e) => setEditData(prev => ({...prev, expectation_max: parseInt(e.target.value) || 20}))}
                  />
                </div>
              </div>
              <div>
                <Label>Comment</Label>
                <Textarea 
                  value={editData.comment || ''}
                  onChange={(e) => setEditData(prev => ({...prev, comment: e.target.value}))}
                  placeholder="Add comments..."
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Visits</Label>
                  <Input 
                    type="number" 
                    value={editData.total_visits || 0}
                    onChange={(e) => setEditData(prev => ({...prev, total_visits: parseInt(e.target.value) || 0}))}
                  />
                </div>
                <div>
                  <Label>Avg Time (min)</Label>
                  <Input 
                    type="number" 
                    value={editData.average_time_per_visit || 0}
                    onChange={(e) => setEditData(prev => ({...prev, average_time_per_visit: parseInt(e.target.value) || 0}))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Expectation Min</Label>
                  <Input 
                    type="number" 
                    value={editData.expectation_min || 13}
                    onChange={(e) => setEditData(prev => ({...prev, expectation_min: parseInt(e.target.value) || 13}))}
                  />
                </div>
                <div>
                  <Label>Expectation Max</Label>
                  <Input 
                    type="number" 
                    value={editData.expectation_max || 18}
                    onChange={(e) => setEditData(prev => ({...prev, expectation_max: parseInt(e.target.value) || 18}))}
                  />
                </div>
              </div>
              <div>
                <Label>Comment</Label>
                <Textarea 
                  value={editData.comment || ''}
                  onChange={(e) => setEditData(prev => ({...prev, comment: e.target.value}))}
                  placeholder="Add comments..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={editing}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={editing}>
              {editing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete All {bulkDeleteType === 'capacity' ? 'Capacity' : 'Productivity'} Records
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete ALL {bulkDeleteType === 'capacity' ? 'capacity' : 'productivity'} records? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-red-800 font-medium">Warning: This will permanently delete:</p>
            <ul className="list-disc list-inside text-red-700 mt-2">
              <li>{bulkDeleteType === 'capacity' ? capacityData.length : clinicians.length} records</li>
              <li>All historical data for this section</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting}>
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete All Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Week Dialog */}
      <Dialog open={showNewWeekDialog} onOpenChange={setShowNewWeekDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-emerald-500" />
              Start New Week
            </DialogTitle>
            <DialogDescription>
              Create a new week entry for {newWeekType === 'capacity' ? 'capacity' : 'productivity'} data
            </DialogDescription>
          </DialogHeader>
          
          {loadingNewWeek ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="text-muted-foreground mt-2">Calculating next week dates...</p>
            </div>
          ) : newWeekDates ? (
            <div className="py-4 space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <p className="font-medium text-emerald-800">Next Week Period:</p>
                <p className="text-emerald-700 text-lg mt-1">
                  {newWeekDates.period_start} to {newWeekDates.period_end}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Click "Start Entry" to open the add dialog with these dates pre-filled.
              </p>
            </div>
          ) : (
            <div className="py-4 text-center text-muted-foreground">
              No dates available
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewWeekDialog(false)}>
              Cancel
            </Button>
            <Button onClick={startNewWeekEntry} disabled={loadingNewWeek || !newWeekDates}>
              <Plus className="h-4 w-4 mr-1" />
              Start Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
